import { useState } from 'react'
import { BookOpen, Zap, Plus, Trash2, Play } from 'lucide-react'
import useStore from '../store/useStore'

export default function StoryboardBuilder() {
  const { characters, addNotification } = useStore()
  const [script, setScript] = useState('')
  const [sceneCount, setSceneCount] = useState(4)
  const [storyboard, setStoryboard] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [title, setTitle] = useState('')

  const handleGenerateStoryboard = async () => {
    if (!script.trim()) {
      addNotification({ type: 'error', message: 'Paste your script first.' })
      return
    }
    setGenerating(true)
    addNotification({ type: 'info', message: 'Claude is building your storyboard...' })

    // Placeholder — will integrate with Claude API in Phase 3
    setTimeout(() => {
      const mockStoryboard = Array.from({ length: sceneCount }, (_, i) => ({
        scene: i + 1,
        type: i % 3 === 0 ? 'B-ROLL' : 'CHARACTER',
        timestamp: `${i * 8}-${(i + 1) * 8}s`,
        action: `Scene ${i + 1} action description here`,
        startFrame: `Scene ${i + 1} start frame — describe first visual`,
        endFrame: `Scene ${i + 1} end frame — describe last visual`,
        fluxPrompt: `[TRIGGER] ${i % 3 === 0 ? '' : 'claymation style, '}scene ${i + 1} description`,
        ltxPrompt: `Character performs action, ${i % 2 === 0 ? 'camera pushes in slowly' : 'static camera'}, warm lighting`,
      }))
      setStoryboard(mockStoryboard)
      setGenerating(false)
      addNotification({ type: 'success', message: `${sceneCount}-scene storyboard ready.` })
    }, 1500)
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">

      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Storyboard Builder</h2>
        <p className="text-slate-400 text-sm mt-1">Paste your script. Claude breaks it into scenes with FLUX and LTX-2 prompts.</p>
      </div>

      <div className="grid grid-cols-5 gap-6">

        {/* Input panel */}
        <div className="col-span-2 space-y-4">
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Script Input</h3>

            <div>
              <label className="label">Video Title</label>
              <input className="input" placeholder="e.g. S-Corp Tax Strategy 2026" value={title}
                onChange={e => setTitle(e.target.value)} />
            </div>

            <div className="mt-3">
              <label className="label">Script</label>
              <textarea
                rows={10}
                className="input resize-none text-sm"
                placeholder="Paste your full video script here...

Example:
Hey, if you're running a business and paying taxes as a sole proprietor, you're probably leaving thousands on the table every year..."
                value={script}
                onChange={e => setScript(e.target.value)}
              />
            </div>

            <div className="mt-3">
              <label className="label">Number of Scenes</label>
              <div className="flex gap-2">
                {[4, 6, 8, 10, 15, 20, 30].map(n => (
                  <button key={n} onClick={() => setSceneCount(n)}
                    className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      sceneCount === n
                        ? 'bg-brand-500 border-brand-400 text-white'
                        : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {sceneCount} scenes × 8s = ~{sceneCount * 8}s video
              </p>
            </div>

            <div className="mt-4">
              <label className="label">Character</label>
              <select className="input text-sm">
                <option value="">No character (B-roll only)</option>
                {characters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="text-xs font-medium text-green-400">Estimated cost</div>
              <div className="text-lg font-bold text-white mt-0.5">
                ${((sceneCount * 2 * 0.0018) + (sceneCount * 0.011) + 0.05).toFixed(2)}
              </div>
              <div className="text-xs text-slate-400">
                {sceneCount * 2} frames + {sceneCount} clips + voice + assembly
              </div>
            </div>

            <button
              onClick={handleGenerateStoryboard}
              disabled={generating || !script.trim()}
              className="btn-primary w-full justify-center mt-4"
            >
              {generating ? <><Zap size={15} className="generation-spinner" /> Building Storyboard...</> : <><Zap size={15} /> Generate Storyboard</>}
            </button>
          </div>
        </div>

        {/* Storyboard output */}
        <div className="col-span-3">
          {!storyboard ? (
            <div className="card p-8 text-center h-full flex flex-col items-center justify-center">
              <BookOpen size={48} className="text-slate-600 mb-4" />
              <h3 className="text-white font-medium mb-2">Storyboard will appear here</h3>
              <p className="text-slate-400 text-sm">Paste your script and hit Generate Storyboard.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">{title || 'Untitled Video'} — {sceneCount} scenes</h3>
                <button className="btn-primary text-xs py-1.5">
                  <Play size={13} /> Generate All
                </button>
              </div>
              {storyboard.map(scene => (
                <SceneCard key={scene.scene} scene={scene} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SceneCard({ scene }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-7 h-7 rounded-full bg-brand-500/20 text-brand-300 text-xs font-bold flex items-center justify-center">
          {scene.scene}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              scene.type === 'B-ROLL'
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
            }`}>{scene.type}</span>
            <span className="text-xs text-slate-500">{scene.timestamp}</span>
          </div>
        </div>
        <button className="btn-primary text-xs py-1">
          <Zap size={11} /> Generate
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-slate-500 uppercase tracking-wider mb-1">Start Frame</div>
          <div className="text-slate-300">{scene.startFrame}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider mb-1">End Frame</div>
          <div className="text-slate-300">{scene.endFrame}</div>
        </div>
        <div className="col-span-2">
          <div className="text-slate-500 uppercase tracking-wider mb-1">Motion Description</div>
          <div className="text-slate-300">{scene.ltxPrompt}</div>
        </div>
      </div>
    </div>
  )
}
